import { Button } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {useNavigate,useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import * as XLSX from 'xlsx';
import { getPOList, getPRList } from '../../api';
import { default as StyledButton } from '../../components/Common/StyledButton';
import { AmountCell } from '../../components/CustomComponents/AmountCell';
import { default as DateFilter } from '../../components/CustomComponents/DateFilter';
import { getClientBranchListReq } from '../../service/branchService';
import { getUserWarehouseListReq } from '../../service/usersService';
import { setBreadcrumbItems } from '../../store/actions';
import MultiSelect from '../DummyTable/MultiSelect';
import SingleSelect from '../DummyTable/SingleSelect';
import Table from '../DummyTable/Table';
import './styles/reports.scss';
import useAuth from '../../hooks/useAuth'
import RequireUserType from '../../routes/middleware/requireUserType';
import { MODULES_ENUM, PERMISSIONS_ENUM, USER_TYPES_ENUM } from '../../utility/constants';

const defaultPageSize = 10;
const POStatuses = [
  'draft',
  'published',
  'approved',
  'rejected',
  'sent',
  'accepted',
  'declined',
  'deleted',
];
const PRStatuses = ['draft', 'published', 'approved', 'rejected'];

const ByPRPO = (props) => {
  const [searchParams,setSearchParams]=useSearchParams()
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const status = searchParams.get('status');
  const urlWarehouseId = searchParams.get('warehouseId');
  const urlBranchId = searchParams.get('branchId');
  const {selectedBranch,selectedWarehouse,auth}=useAuth();
  const { reportType, userType } = props;
  const isPR = reportType === 'PR';
  const navigate = useNavigate();
  const firstRenderRef = useRef(true);
  const [sorting, setSorting] = useState(() => {
    const sortField = searchParams.get('sortField');
    const sortOrder = searchParams.get('sortOrder');
    return [
      {
        id: sortField || 'createdAt',
        desc: sortOrder ? sortOrder === 'desc' : true,
      },
    ] ;
  });
  const [pagination, setPagination] = useState(() => ({
    pageIndex:Math.max(Number(searchParams.get('page')) -1,0),
    pageSize: Number(searchParams.get('limit')) || defaultPageSize,
  }));

  //fetching the startDate,endDate from the dashboard  
 

  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const allowedStatuses = reportType === 'PO' ? POStatuses : PRStatuses;
  const [statuses, setStatuses] = useState(() =>
    status
      ? status.split(',').map(s => s.trim().toLowerCase()).filter(s => allowedStatuses.includes(s))
      : []
  );
  
  const [tempStatuses, setTempStatuses] = useState(() =>
    status
      ? status.split(',').map(s => s.trim().toLowerCase()).filter(s => allowedStatuses.includes(s))
      : []
    );
  const [tempWarehouseId, setTempWarehouseId] = useState(null);
  const [tempBranchId, setTempBranchId] = useState(null);
  const [tempDate, setTempDate] = useState(() => {
    if (startDate && endDate) {
      return {
        start: startDate,
        end: endDate,
      };
    }
    return null;
  });
  const [tempSelectedWarehouses, setTempSelectedWarehouses] = useState([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [warehouseList, setWarehouseList] = useState([]);
  const [warehouseId, setSelectedWarehouseId] = useState(urlWarehouseId||null);

  const [branchList, setBranchList] = useState([]);
  const [branchId, setSelectedBranchId] = useState(urlBranchId||null);

  const [date, setDate] = useState(() => {
    if (startDate && endDate) {
      return {
        start: startDate,
        end: endDate,
      };
    }
    return null;
  });
  useEffect(() => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);

        params.set('page', String(pagination.pageIndex + 1));
        params.set('limit', String(pagination.pageSize));

        return params;
      }, { replace: true });
  }, [pagination, setSearchParams]);

  useEffect(() => {
    const sort = sorting?.[0];

    if (!sort) return;
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);

      params.set('sortField', sort.id);
      params.set('sortOrder', sort.desc ? 'desc' : 'asc');

      return params;
    }, { replace: true });
  }, [sorting, setSearchParams]);
  
  const [isExporting, setExporting] = useState(false);
  const queryAppliedRef = useRef(false);

  const getColumns = (reportType, userType) => {
    const isPR = reportType === 'PR';
    return [
      {
        accessorKey: isPR?'createdAt':'poDate',
        header: () => <span>{'Date'}</span>,
        enableSorting: true,
        defaultSortIcon: true,
        cell: (value) => (
          <span>
            {new Date(value.getValue()).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })}
          </span>
        ),
      },
      !isPR
        ? {
          accessorKey: 'prNumber',
          header: () => <span>PR Number</span>,
          enableSorting: true,
          cell: (value) => <span>{value.getValue()}</span>,
        }
        : null,
      {
        accessorKey: isPR ? 'prNumber' : 'poNumber',
        header: () => <span>{isPR ? 'PR Number' : 'PO Number'}</span>,
        enableSorting: true,
        cell: ({ row, getValue }) => {
          const id = row.original?.id;
          const number = getValue();

          return (
            <span
              onClick={() =>
                navigate(
                  isPR
                    ? `/purchase-requisitions/${id}`
                    : `/purchase-orders/${id}`,
                )
              }
              style={{ cursor: 'pointer' }}
            >
              {number || '-'}
            </span>
          );
        },
      },
      {
        accessorKey: isPR ? 'prStatus' : 'poStatus',
        header: () => <span>{isPR ? 'PR Status' : 'PO Status'}</span>,
        cell: ({ getValue }) => {
          const status = getValue()?.toLowerCase() || '-';

          const colorMap = {
            draft: 'gray',
            published: 'goldenrod',
            approved: 'green',
            rejected: 'red',
            sent: 'blue',
            accepted: 'green',
            declined: 'orange',
            deleted: 'red',
          };

          return (
            <span
              style={{ color: colorMap[status] || 'black', fontWeight: 600 }}
            >
              {status.toUpperCase()}
            </span>
          );
        },
      },
      {
          accessorKey: 'clientName',
          header: () => <span>Customer</span>,
          enableSorting: true,
          cell: (value) => <span>{value.getValue()}</span>,
      },
       {
          accessorKey: 'warehouse',
          header: () => <span>Warehouse</span>,
          enableSorting: true,
          cell: (value) => <span>{value.getValue()}</span>,
      },
      {
        accessorKey:'shippingBranch',
        header: () => <span>Shipping Branch</span>,
        cell: (value) => <span>{value.getValue()}</span>,
      },
      {
        accessorKey: 'subtotal',
        header: () => <span>Subtotal</span>,
        cell: ({ getValue }) => <AmountCell value={getValue()} />,
      },
      {
        accessorKey: 'gst',
        header: () => <span>GST(%)</span>,
        cell: (value) => <span>{value.getValue()}%</span>,
      },
      {
        accessorKey: 'total',
        header: () => <span>{isPR ? 'PR Total' : 'PO Total'}</span>,
        cell: ({ getValue }) => <AmountCell value={getValue()} />,
      },
      {
        accessorKey:isPR?'PRApprovedBy':'POApprovedBy',
        header: () => <span>Approved By</span>,
        cell: (value) => <span>{value.getValue() || '-'}</span>,
      },
    ].filter((ele) => ele !== null);
  };

 

  const searchAllWareHouses = async () => {
    try {
      const response = await getUserWarehouseListReq();
      const { warehouses } = response?.payload;
      const list = warehouses.map((wh) => {
        const { _id, name } = wh;
        return {
          id: _id,
          name,
        };
      });
      setWarehouseList(list);
    } catch (error) {
      console.log(error);
    }
  };

  const getBranches = async () => {
    try {
      const response = await getClientBranchListReq();
      const { branches } = response?.payload;
      const list = branches.map((wh) => {
        const { _id, name } = wh;
        return {
          id: _id,
          name,
        };
      });
      setBranchList(list);
    } catch (error) {
      console.log(error);
    }
  };

  const handleApply = () => {
  setStatuses(tempStatuses);
  setSelectedWarehouseId(tempWarehouseId);
  setSelectedBranchId(tempBranchId);
  setDate(tempDate);
  setSelectedWarehouses(tempSelectedWarehouses);

  setSearchParams((prev) => {
    const params = new URLSearchParams(prev);

    if (tempStatuses.length > 0) {
      params.set('status', tempStatuses.join(','));
    } else {
      params.delete('status');
    }

    
    if (tempDate?.start && tempDate?.end) {
      params.set('startDate', tempDate.start);
      params.set('endDate', tempDate.end);
    } else {
      params.delete('startDate');
      params.delete('endDate');
    }
    if (tempWarehouseId) {
      params.set('warehouseId', tempWarehouseId);
    } else {
      params.delete('warehouseId');
    }
    if (tempBranchId) {
      params.set('branchId', tempBranchId);
    } else {
      params.delete('branchId');
    }

    return params;
  }, { replace: true });

  resetPagination();
};
  useEffect(() => {
    if (userType === 'admin') {
      searchAllWareHouses();
    }
    if (userType === 'client') {
      getBranches();
    }
  }, []);

  const effectCalled = useRef(false);

  const breadcrumbItems =
    reportType === 'PO'
      ? [
        { title: 'Dashboard', link: '/dashboard' },
        { title: 'By PO', link: '#' },
      ]
      : [
        { title: 'Dashboard', link: '/dashboard' },
        { title: 'By PR', link: '#' },
      ];

  useEffect(() => {
    if (!effectCalled.current) {
      props.setBreadcrumbItems(
        reportType === 'PO' ? 'By PO' : 'By PR',
        breadcrumbItems,
      );
      effectCalled.current = true;
    }
  }, []);





  const getPRPOList = async (page, limit) => {
    const sortObj = sorting?.[0];
    const body = {
      page,
      limit,
      statusIn: statuses || [],
      sortField: sortObj?.id || '',
      sortOrder: sortObj?.desc ? 'desc' : 'asc',
      warehouseId: warehouseId || selectedWarehouse,
      clientId:
        userType === 'client'
          ? JSON.parse(localStorage.getItem('user')).clientId
          : '',
      branchId: branchId ||selectedBranch,
      warehouseIds:
        selectedWarehouses.length > 0
          ? selectedWarehouses.map((w) => w.id)
          : [],
    };

    if (date?.start && date?.end) {
      body.startDate = date.start;
      body.endDate = date.end;
    }
    const res =
      reportType === 'PO' ? await getPOList(body) : await getPRList(body);

    return res;
  };

  useEffect(() => {
    setPagination((p) => ({
      ...p,
      pageIndex: pagination.pageIndex,
    }));
  }, [sorting]);

  useEffect(() => {
    if (firstRenderRef.current && statuses.length === 0) {
      firstRenderRef.current = false;
      return;
    }

    const fetchList = async () => {
      try {
        setIsLoading(true);
        const res = await getPRPOList(
          pagination.pageIndex + 1,
          pagination.pageSize,
        );
        if (res.success) {
          const { data, totalCount } = res.payload;
          setRows(data);
          setTotal(totalCount);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchList();
  }, [
    pagination,
    statuses,
    sorting,
    warehouseId,
    branchId,
    date,
    selectedWarehouses,
    selectedBranch,
    selectedWarehouse
  ]);

  const columns = useMemo(() => getColumns(reportType, userType), [reportType]);
  const clientColumnDefs = columns.filter(colDef => colDef.accessorKey !== "clientName"&&colDef.accessorKey!=="warehouse")
  const clientUserColumnDefs = clientColumnDefs.filter(colDef => colDef.accessorKey!== "total"&&colDef.accessorKey!=="gst"&&colDef.accessorKey!=="subtotal")

  const resetPagination = () => {
    setPagination({
      pageIndex: 0,
      pageSize: defaultPageSize,
    });
  };

  const handleStatusChange = (e) => {
    const v = e.target.value;
    setTempStatuses(typeof v === 'string' ? v.split(',') : v);
  };

  const handleWarehouseChange = (e) => {
    setTempWarehouseId(e.target.value);
  };

  const handleBranchChange = (e) => {
    setTempBranchId(e.target.value);
  };

  const resetFilters = () => {
    setSelectedWarehouseId(null);
    setStatuses([]);
    setDate(null);
    setSelectedBranchId(null);
    setSelectedWarehouses([]);

    setTempWarehouseId(null);
    setTempStatuses([]);
    setTempDate(null);
    setTempBranchId(null);
    setTempSelectedWarehouses([]);

    setSearchParams({},{replace:true})

    resetPagination();
  };

  const onDateChange = (data) => {
    setTempDate(data);
  };

  const downloadItemsExcel = async () => {
    try {
      setExporting(true);
      const response = await getPRPOList();
      if (response.success) {
        const { data } = response.payload;
      let exportColumns;

      if (userType === "admin") {
        exportColumns = columns;
      } else if (
        auth?.permissions?.some(
          (p) =>
            p.module === MODULES_ENUM.CLIENT_REPORTS &&
            [PERMISSIONS_ENUM.READ, PERMISSIONS_ENUM.CREATE].every((perm) =>
              p.operations.includes(perm)
            )
        )
      ) {
        exportColumns = clientColumnDefs;
      } else {
        exportColumns = clientUserColumnDefs;
      }

      const flatItems = data.map((row) => {
        const excelRow = {};

        exportColumns.forEach((col) => {
          const header = col.header()?.props?.children || col.accessorKey;

          excelRow[header] = col.exportValue
            ? col.exportValue(row)
            : row[col.accessorKey];
        });

        return excelRow;
      });
        // Convert to worksheet
        const worksheet = XLSX.utils.json_to_sheet(flatItems);

        // Create workbook and add worksheet
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'PR_PO');

        // Download as Excel file
        XLSX.writeFile(
          workbook,
          reportType === 'PO'
            ? 'Purchase Order.xlsx'
            : 'Purchase Requisition.xlsx',
        ); 
        toast.success('PR/PO Exported Successfully!');
        setExporting(false);
      } else {
        console.error('Failed to fetch PR/PO');
        setExporting(false);
      }
    } catch (error) {
      console.error('Error downloading Excel:', error);
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="filter-wrapper">
        <div className="filter-toolbar">
          <div className="filter-row">
            <DateFilter onDateChange={onDateChange} />

            {userType === 'admin' && (
              <SingleSelect
                label="Warehouses"
                list={warehouseList}
                selectedValue={tempWarehouseId}
                handleChange={handleWarehouseChange}
              />
            )}

            {userType === 'client' && (
              <SingleSelect
                label="Branches"
                list={branchList}
                selectedValue={tempBranchId}
                handleChange={handleBranchChange}
              />
            )}

            <MultiSelect
              label="Status"
              list={reportType === 'PO' ? POStatuses : PRStatuses}
              selectedValues={tempStatuses}
              handleChange={handleStatusChange}
            />

            <Button
              variant="contained"
              sx={{ backgroundColor: '#0053FF', height: 40 }}
              onClick={handleApply}
            >
              Apply
            </Button>

            <Button
              variant="outlined"
              sx={{ height: 40 }}
              onClick={resetFilters}
            >
              Reset
            </Button>
          </div>

          <StyledButton
            color="primary"
            className="export-btn"
            onClick={downloadItemsExcel}
            isLoading={isExporting}
          >
            <div className="export-btn-content">
              <i className="ion ion-md-cloud-download mx-2"></i>
              Export
            </div>
          </StyledButton>
        </div>
      </div>
      <RequireUserType userType={USER_TYPES_ENUM.ADMIN}>
       <Table
        data={rows}
        columns={columns}
        manualPagination
        rowCount={total}
        sorting={sorting}
        pagination={pagination}
        setSorting={setSorting}
        setPagination={setPagination}
        isLoading={isLoading}
      />
      </RequireUserType>
      <RequireUserType userType={USER_TYPES_ENUM.CLIENT}>
       <Table
        data={rows}
        columns={auth?.permissions?.some(
          (p) =>
            p.module === MODULES_ENUM.CLIENT_REPORTS &&
            [PERMISSIONS_ENUM.READ, PERMISSIONS_ENUM.CREATE].every((perm) => p.operations.includes(perm))
            )
        ? clientColumnDefs:auth?.permissions?.some(
          (p) =>
            p.module === MODULES_ENUM.CLIENT_REPORTS && p.operations.includes(PERMISSIONS_ENUM.READ)
          )? clientUserColumnDefs: []}
        manualPagination
        rowCount={total}
        sorting={sorting}
        pagination={pagination}
        setSorting={setSorting}
        setPagination={setPagination}
        isLoading={isLoading}
      />
      </RequireUserType>
    </div>
  );
};

export default connect(null, { setBreadcrumbItems })(ByPRPO);
